import React, { useEffect, useState, useRef } from "react";
import api from '../services/api';
import { FaPrint, FaExternalLinkAlt, FaFileExport, FaSpinner } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { LoadPanel } from "devextreme-react/load-panel";

const ReportViewerIFrame = () => {
  const { id } = useParams();
  const pdfUrl = `/Reports/applicant_visa/${id}`;
  const baseExportUrl = "/Reports/applicant/export";

  const [state, setState] = useState({
    showDropdown: false,
    iframeSrc: null,
    isLoading: true,
    isExporting: false,
    isMobile: false,
    pdfBlobUrl: null,
    error: null
  });

  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Check if mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth < 768;
    };
    
    setState(prev => ({ ...prev, isMobile: checkIfMobile() }));
  }, []);

  // Fetch PDF report
  useEffect(() => {
    const abortController = new AbortController();

    const fetchPdf = async () => {
      if (!pdfUrl || !id) {
        setState(prev => ({ ...prev, error: "URL or ID is missing", isLoading: false }));
        return;
      }

      try {
        const response = await api.get(pdfUrl, {
          responseType: 'blob',
          signal: abortController.signal,
          timeout: 60000
        });

        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/pdf'
        });
        const blobUrl = URL.createObjectURL(blob);

        if (state.isMobile) {
          window.open(blobUrl, '_blank');
          setState(prev => ({ ...prev, pdfBlobUrl: blobUrl, isLoading: false }));
        } else {
          setState(prev => ({ 
            ...prev, 
            pdfBlobUrl: blobUrl, 
            iframeSrc: `${blobUrl}#zoom=100`, 
            isLoading: false 
          }));
        }

      } catch (error) {
        if (!abortController.signal.aborted) {
          setState(prev => ({ 
            ...prev, 
            error: error.message || "Failed to load report",
            isLoading: false 
          }));
          console.error("PDF fetch error:", error);
        }
      }
    };

    fetchPdf();

    return () => {
      abortController.abort();
      if (state.pdfBlobUrl) URL.revokeObjectURL(state.pdfBlobUrl);
    };
  }, [pdfUrl, id, state.isMobile]);

  const getFileExtension = (format) => {
    switch(format) {
      case 'pdf': return 'pdf';
      case 'xlsx': return 'xlsx';
      case 'docx': return 'docx';
      case 'image': return 'png';  // Changed to png
      default: return format;
    }
  };

  const handlePrint = () => {
    if (state.isMobile && state.pdfBlobUrl) {
      window.open(state.pdfBlobUrl, '_blank');
    } else if (state.iframeSrc) {
      const printWindow = window.open(state.iframeSrc, "_blank");
      if (printWindow) {
        printWindow.focus();
        printWindow.onload = () => printWindow.print();
      }
    }
  };

  const handleExport = async (format) => {
    setState(prev => ({ ...prev, isExporting: true, showDropdown: false }));

    try {
      const response = await api.get(
        `${baseExportUrl}?format=${format}&applicantId=${id}`,
        { responseType: 'blob', timeout: 60000 }
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const downloadUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `applicant_${id}_report.${getFileExtension(format)}`;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }, 100);

    } catch (error) {
      setState(prev => ({ ...prev, error: "Export failed. Please try again." }));
      console.error("Export error:", error);
    } finally {
      setState(prev => ({ ...prev, isExporting: false }));
    }
  };

  const toggleDropdown = () => {
    setState(prev => ({ ...prev, showDropdown: !prev.showDropdown }));
  };

  const closeDropdown = () => {
    setState(prev => ({ ...prev, showDropdown: false }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Styles
  const buttonStyle = {
    border: "none",
    backgroundColor: "#323232",
    color: "white",
    padding: "10px",
    borderRadius: "5px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
  };

  const dropdownMenuStyle = {
    position: "absolute",
    top: "45px",
    left: "0",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    borderRadius: "5px",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    width: "150px",
  };

  const dropdownItemStyle = {
    padding: "10px 15px",
    border: "none",
    backgroundColor: "white",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "14px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    transition: "background 0.2s",
    ":hover": {
      backgroundColor: "#ffa800"
    }
  };

  const mobileButtonStyle = {
    border: "none",
    backgroundColor: "#323232",
    color: "white",
    padding: "12px 15px",
    borderRadius: "5px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    fontSize: "16px",
  };

  // Mobile view
  if (state.isMobile) {
    return (
      <div className="mobile-container">
        <h3>Report Viewer</h3>
        <p>The report should open in a new tab automatically.</p>
        
        {state.pdfBlobUrl && (
          <div className="mobile-controls">
            <button onClick={() => window.open(state.pdfBlobUrl, '_blank')} style={mobileButtonStyle}>
              <FaExternalLinkAlt style={{ marginRight: 8 }} />
              Open Report Again
            </button>
            
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                style={mobileButtonStyle}
                onClick={toggleDropdown}
                disabled={state.isExporting}
              >
                {state.isExporting ? (
                  <FaSpinner className="spin" style={{ marginRight: 8 }} />
                ) : (
                  <FaFileExport style={{ marginRight: 8 }} />
                )}
                Export Report
              </button>
              {state.showDropdown && (
                <div style={dropdownMenuStyle}>
                  {['pdf', 'xlsx', 'docx', 'csv'].map(format => (
                    <button
                      key={format}
                      style={dropdownItemStyle}
                      onClick={() => handleExport(format)}
                    >
                      {format === 'pdf' && '📄 PDF'}
                      {format === 'xlsx' && '📊 Excel'}
                      {format === 'docx' && '📜 Word'}
                      {format === 'csv' && '📑 CSV'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button onClick={handlePrint} style={mobileButtonStyle}>
              <FaPrint style={{ marginRight: 8 }} />
              Print Report
            </button>
          </div>
        )}
        
        {state.error && <div className="error-message">{state.error}</div>}
        
        <LoadPanel
          visible={state.isLoading || state.isExporting}
          showIndicator={true}
          showPane={true}
          shading={true}
          hideOnOutsideClick={false}
          message={state.isExporting ? "Exporting..." : "Loading report..."}
          position={{ of: containerRef.current }}
        />
      </div>
    );
  }

  // Desktop view
  return (
    <div className="desktop-container" ref={containerRef}>
      <LoadPanel
        visible={state.isLoading || state.isExporting}
        showIndicator={true}
        showPane={true}
        shading={true}
        hideOnOutsideClick={false}
        message={state.isExporting ? "Exporting..." : "Loading report..."}
        position={{ of: containerRef.current }}
      />

      <div className="toolbar">
        <button onClick={handlePrint} style={buttonStyle}>
          <FaPrint size={20} />
        </button>

        <a href={state.iframeSrc} target="_blank" rel="noopener noreferrer">
          <button style={buttonStyle}>
            <FaExternalLinkAlt size={20} />
          </button>
        </a>

        <div style={{ position: "relative" }} ref={dropdownRef}>
          <button
            style={buttonStyle}
            onClick={toggleDropdown}
            disabled={state.isExporting}
          >
            {state.isExporting ? (
              <FaSpinner className="spin" size={20} />
            ) : (
              <FaFileExport size={20} />
            )}
          </button>
          {state.showDropdown && (
            <div
              style={dropdownMenuStyle}
              onMouseEnter={() => setState(prev => ({ ...prev, showDropdown: true }))}
              onMouseLeave={closeDropdown}
            >
              {['pdf', 'xlsx', 'docx', 'html', 'csv', 'image'].map(format => (
                <button
                  key={format}
                  style={dropdownItemStyle}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "#ffa800"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                  onClick={() => handleExport(format)}
                >
                  {format === 'pdf' && '📄 PDF'}
                  {format === 'xlsx' && '📊 Excel'}
                  {format === 'docx' && '📜 Word'}
                  {format === 'html' && '🌐 HTML'}
                  {format === 'csv' && '📑 CSV'}
                  {format === 'image' && '🖼️ Image'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="iframe-container">
        {state.iframeSrc ? (
          <iframe
            src={state.iframeSrc}
            title="Report Viewer"
            width="100%"
            height="100%"
            style={{
              border: "none",
              backgroundColor: "#fff",
            }}
          />
        ) : state.error ? (
          <div className="error-message">{state.error}</div>
        ) : null}
      </div>

      <style jsx>{`
        .mobile-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 100vh;
          padding: 20px;
          text-align: center;
          background-color: #f5f5f5;
        }
        
        .mobile-controls {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 30px;
          width: 100%;
          max-width: 300px;
        }
        
        .desktop-container {
          display: flex;
          flex-direction: column;
          height: 100vh;
          width: 100vw;
          background-color: #fff;
          position: relative;
        }
        
        .toolbar {
          padding: 10px;
          background-color: #f5f5f5;
          border-bottom: 1px solid #ddd;
          text-align: center;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 15px;
          position: relative;
          z-index: 1;
        }
        
        .iframe-container {
          flex-grow: 1;
          overflow: hidden;
          position: relative;
        }
        
        .error-message {
          color: #d32f2f;
          padding: 20px;
          text-align: center;
          background-color: #ffebee;
          border-radius: 4px;
          margin: 20px;
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReportViewerIFrame;